import type React from 'react';
import { useState } from 'react';

import { ExpandMoreRounded } from '@mui/icons-material';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { OrdAutocompleteList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { ResourceContextProvider, useCanAccess, useDelete, useTranslate } from 'react-admin';

import { Dropzone } from 'src/components/dropzone';
import { uploadFile } from 'src/lib/tus';
import { getErrorMessage } from 'src/views/Resources/admin/soundfiles/utils';

import { useIngordDataProvider } from '../hooks/useIngordDataProvider';

import type { Soundfile } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface SoundfileListProps {
  soundfile: Identifier;
  label: string;
  setSoundfile: (soundfile: Soundfile | null) => void;
}

/** Strips the extension and appends a short unique suffix, so the upload never collides with an existing name. */
const uniqueSoundfileName = (file: File): string => {
  const base = file.name.replace(/\.[^./]+$/, '');
  return `${base} ${Date.now().toString(36)}`;
};

const SoundfileList = ({ soundfile, label, setSoundfile }: SoundfileListProps): React.ReactElement => {
  const translate = useTranslate();
  const dataProvider = useIngordDataProvider();
  const [deleteOne] = useDelete();
  const { canAccess: canUploadSoundfile } = useCanAccess({ resource: Resources.soundfiles, action: 'create' });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fetchData = (id: string): void => {
    if (soundfile !== undefined && soundfile !== null) {
      dataProvider
        .getOne<Soundfile>('soundfiles', { id })
        .then((data) => {
          setSoundfile(data.data);
        })
        .catch(() => {
          setSoundfile(null);
        });
    } else {
      setSoundfile(null);
    }
  };

  const onFileSelected = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setUploadError(null);
    setUploading(true);

    let created: Soundfile | undefined;
    try {
      const { data } = await dataProvider.create<Soundfile>('soundfiles', {
        data: { name: uniqueSoundfileName(file), labels: [] },
      });
      created = data;
      await uploadFile(file, created.id, Resources.soundfiles);
      setSoundfile(created);
      setUploadOpen(false);
    } catch (error) {
      setUploadError(getErrorMessage(error));
      if (created !== undefined) {
        await deleteOne(Resources.soundfiles, { id: created.id }).catch(() => {});
      }
    } finally {
      setUploading(false);
    }
  };

  const onFileRejected = (rejected: boolean): void => {
    setUploadError(rejected ? 'resources.soundfiles.errors.fileInvalid' : null);
  };

  return (
    <Box>
      <OrdAutocompleteList resource='soundfiles' source='name' value={soundfile} label={label} onChange={fetchData} />
      {soundfile === '' && <Box color='error.main'>{translate('callflows.nodes.ivr.soundfileErrorEmpty')}</Box>}
      {canUploadSoundfile === true && (
        <>
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            onClick={() => {
              setUploadOpen(!uploadOpen);
            }}
            sx={{ cursor: 'pointer', width: 'fit-content', my: 2 }}
          >
            <Typography variant='body2' color='primary'>
              {translate('callflows.interface.soundfileUpload.toggle')}
            </Typography>
            <ExpandMoreRounded
              color='primary'
              fontSize='small'
              sx={{ transform: uploadOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            />
          </Stack>
          {uploadOpen && (
            <Box mt={1}>
              <ResourceContextProvider value={Resources.soundfiles}>
                <Dropzone
                  accept={{ 'audio/mpeg': ['.mp3'], 'audio/wav': ['.wav'] }}
                  hasError={uploadError != null}
                  onFileSelected={(file) => {
                    onFileSelected(file).catch(() => {});
                  }}
                  onFileRejected={onFileRejected}
                />
              </ResourceContextProvider>
              {uploading && (
                <Box mt={1}>
                  <LinearProgress />
                  <Typography variant='caption'>
                    {translate('callflows.interface.soundfileUpload.uploading')}
                  </Typography>
                </Box>
              )}
              {uploadError != null && (
                <Typography variant='caption' color='error'>
                  {translate(uploadError)}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SoundfileList;
